import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import ProcessMimic from "@/components/ProcessMimic";
import ScadaOverview from "@/components/ScadaOverview";
import DcsFaceplates from "@/components/DcsFaceplates";
import AlarmPanel from "@/components/AlarmPanel";
import ObjectivesPanel from "@/components/ObjectivesPanel";
import EventTimeline from "@/components/EventTimeline";
import ControlPanel from "@/components/ControlPanel";
import ScenarioBriefing from "@/components/ScenarioBriefing";
import ForensicsPanel from "@/components/ForensicsPanel";
import DecisionPanel from "@/components/DecisionPanel";
import ManufacturingMimic from "@/components/ManufacturingMimic";
import PowerMimic from "@/components/PowerMimic";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  ListChecks, Sparkles, Flag, ArrowLeft, Cpu, Network, GaugeCircle, Lightbulb, X, ShieldAlert, AlertOctagon,
} from "lucide-react";

const moduleLabel = { plc_hmi: "PLC + HMI", scada: "SCADA / HMI", dcs: "DCS Control", lab: "Lab" };

export default function Simulator() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [attempt, setAttempt] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState(null);
  const [hintLoading, setHintLoading] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [rail, setRail] = useState("tasks");
  const pollRef = useRef(null);

  const pushHistory = useCallback((tags) => {
    const map = Object.fromEntries(tags.map((t) => [t.tag_id, t.value]));
    setHistory((h) => [...h.slice(-39), map]);
  }, []);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/attempts/${attemptId}`);
      setAttempt(r.data);
      pushHistory(r.data.tags);
    } catch {
      toast.error("Attempt not found");
      navigate("/dashboard");
    }
  }, [attemptId, navigate, pushHistory]);

  useEffect(() => { load(); }, [load]);

  // Live polling of simulation state
  useEffect(() => {
    if (!attempt || attempt.status !== "in_progress") return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/attempts/${attemptId}/state`);
        setAttempt(r.data);
        pushHistory(r.data.tags);
      } catch (e) { console.error("Simulation state poll failed", e); }
    }, 2500);
    return () => clearInterval(pollRef.current);
  }, [attempt?.status, attemptId, pushHistory]); // eslint-disable-line

  const doAction = async (payload) => {
    setBusy(true);
    try {
      const r = await api.post(`/attempts/${attemptId}/action`, payload);
      setAttempt(r.data);
      pushHistory(r.data.tags);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const onWrite = (tag_id, value) => doAction({ action_type: "write_tag", tag_id, value });
  const onAck = (alarm_id) => doAction({ action_type: "ack_alarm", alarm_id });
  const onAnswer = (objective_id, answer_index) => doAction({ action_type: "answer", objective_id, answer_index });

  const getHint = async () => {
    setHintLoading(true);
    try {
      const r = await api.post(`/attempts/${attemptId}/hint`);
      setHint(r.data.hint);
    } catch {
      toast.error("Hint unavailable");
    } finally {
      setHintLoading(false);
    }
  };

  const complete = async () => {
    setCompleting(true);
    try {
      await api.post(`/attempts/${attemptId}/complete`);
      navigate(`/scorecard/${attemptId}`);
    } catch {
      toast.error("Could not complete");
      setCompleting(false);
    }
  };

  if (!attempt) {
    return <div className="min-h-screen bg-background flex items-center justify-center font-mono text-sm text-muted-foreground animate-pulse">LOADING SIMULATION…</div>;
  }

  const done = attempt.status === "completed";
  const completedObjs = attempt.objectives_status.filter((o) => o.completed).length;
  const ModuleIcon = attempt.module === "scada" ? Network : attempt.module === "dcs" ? GaugeCircle : Cpu;
  const decisionQs = attempt.objectives_status.filter((o) => o.kind === "decision");
  const actionTasks = attempt.objectives_status.filter((o) => o.kind !== "decision");
  const isSecurity = attempt.category === "security";
  const activeAlarms = attempt.alarms.filter((a) => a.active);
  const activeCrit = activeAlarms.filter((a) => a.severity === "critical" && !a.acknowledged);

  return (
    <div className="min-h-screen bg-background cr-grid">
      {/* Top control bar */}
      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/90 backdrop-blur-md">
        <div className="max-w-[1600px] mx-auto px-4 lg:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <button data-testid="back-button" onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="font-heading font-semibold text-sm truncate">{attempt.scenario_title}</div>
              <div className="label-ot flex items-center gap-1.5"><ModuleIcon className="w-3 h-3" />{moduleLabel[attempt.module]} · {attempt.difficulty}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 font-mono text-xs text-muted-foreground mr-2">
              <ListChecks className="w-4 h-4 text-primary" />{completedObjs}/{attempt.objectives_status.length} · {attempt.score}/{attempt.max_score}p
            </div>
            <Button data-testid="hint-button" onClick={getHint} disabled={done || hintLoading} variant="outline"
              className="rounded-sm border-border h-9 text-xs font-semibold">
              <Sparkles className="w-3.5 h-3.5 mr-1.5 text-status-warning" />{hintLoading ? "Thinking…" : "AI Hint"}
            </Button>
            <Button data-testid="complete-button" onClick={complete} disabled={done || completing}
              className="rounded-sm h-9 text-xs font-semibold bg-primary hover:bg-primary/90">
              <Flag className="w-3.5 h-3.5 mr-1.5" />{completing ? "Scoring…" : "Complete & Score"}
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 lg:px-6 py-4">
        {/* AI hint banner */}
        {hint && (
          <div data-testid="hint-banner" className="mb-4 bg-status-warning/10 border border-status-warning/40 rounded-md p-3 flex items-start gap-3">
            <Lightbulb className="w-4 h-4 text-status-warning shrink-0 mt-0.5" />
            <p className="text-sm flex-1 text-foreground/90">{hint}</p>
            <button onClick={() => setHint(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Threat / mission briefing */}
        <ScenarioBriefing attempt={attempt} />

        <div className="grid lg:grid-cols-12 gap-4 items-start">
          {/* Main process view */}
          <div className="lg:col-span-8 space-y-4">
            {attempt.plant === "power" ? (
              <PowerMimic tags={attempt.tags} />
            ) : attempt.module === "scada" ? (
              <ScadaOverview tags={attempt.tags} history={history} />
            ) : attempt.module === "dcs" ? (
              <DcsFaceplates tags={attempt.tags} />
            ) : attempt.plant === "manufacturing" ? (
              <ManufacturingMimic tags={attempt.tags} />
            ) : (
              <ProcessMimic tags={attempt.tags} />
            )}
            <ForensicsPanel logs={attempt.logs} />
            <EventTimeline events={attempt.events} />
          </div>

          {/* Right rail — sticky tabbed workspace so tasks/controls/alarms are always one click away */}
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-20 space-y-3">
              {activeCrit.length > 0 && rail !== "alarms" && (
                <button
                  data-testid="alarm-quickbar"
                  onClick={() => setRail("alarms")}
                  className="w-full alarm-critical-blink border border-status-critical rounded-md px-3 py-2 flex items-center gap-2 text-left"
                >
                  <AlertOctagon className="w-4 h-4 text-status-critical shrink-0" />
                  <span className="text-xs font-semibold text-status-critical">
                    {activeCrit.length} unacknowledged critical alarm{activeCrit.length > 1 ? "s" : ""}
                  </span>
                  <span className="ml-auto font-mono text-[10px] text-status-critical">OPEN →</span>
                </button>
              )}
              <Tabs value={rail} onValueChange={setRail}>
                <TabsList className="grid grid-cols-3 w-full bg-card border border-border/60 rounded-md h-auto p-1">
                  <TabsTrigger value="tasks" data-testid="rail-tab-tasks" className="rounded-sm text-xs data-[state=active]:bg-secondary">
                    Tasks
                    {(decisionQs.length + actionTasks.length) > 0 && (
                      <span className="ml-1.5 font-mono text-[10px] text-muted-foreground">
                        {attempt.objectives_status.filter((o) => o.completed).length}/{attempt.objectives_status.length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="controls" data-testid="rail-tab-controls" className="rounded-sm text-xs data-[state=active]:bg-secondary">
                    Controls
                  </TabsTrigger>
                  <TabsTrigger value="alarms" data-testid="rail-tab-alarms" className="rounded-sm text-xs data-[state=active]:bg-secondary">
                    Alarms
                    {activeAlarms.length > 0 && (
                      <span className={`ml-1.5 font-mono text-[10px] px-1.5 rounded-sm ${activeCrit.length > 0 ? "bg-status-critical/20 text-status-critical" : "bg-status-warning/20 text-status-warning"}`}>
                        {activeAlarms.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="tasks" className="space-y-4 mt-3">
                  <DecisionPanel questions={decisionQs} onAnswer={onAnswer} disabled={done || busy} />
                  {actionTasks.length > 0 && (
                    <ObjectivesPanel objectives={actionTasks} title={isSecurity ? "Response Tasks" : "Objectives"} />
                  )}
                  {decisionQs.length === 0 && actionTasks.length === 0 && (
                    <div className="bg-card border border-border/60 rounded-md p-6 text-sm text-muted-foreground">No tasks for this scenario.</div>
                  )}
                </TabsContent>
                <TabsContent value="controls" className="mt-3">
                  <ControlPanel tags={attempt.tags} onWrite={onWrite} disabled={done || busy} />
                </TabsContent>
                <TabsContent value="alarms" className="mt-3">
                  <AlarmPanel alarms={attempt.alarms} onAck={onAck} disabled={done || busy} />
                </TabsContent>
              </Tabs>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
